import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Shield, Zap, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import SEO from '../components/SEO';

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SEO 
        title="About Us"
        description="Learn about Coercive Control Patterns - AI-powered document analysis for identifying manipulation and control patterns in communications."
        keywords="about coercive control patterns, document analysis service, pattern recognition"
        canonical="/about"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <Eye className="w-10 h-10 text-purple-400" />
            <span className="text-2xl font-bold gradient-text serif-display">Coercive Control Patterns</span>
          </div>
          
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center space-x-2 text-purple-400 hover:text-purple-300 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
        </div>

        {/* Main Content */}
        <div className="glass rounded-2xl p-8">
          
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-white mb-6 serif-display">
              Make the Invisible Visible
            </h1>
            <p className="text-2xl text-purple-200 mb-8 leading-relaxed">
              You don't have to prove it alone. We help you make patterns visible — simply, securely, and privately.
            </p>
            <p className="text-xl text-gray-300 leading-relaxed">
              Transform difficult communications into clear, credible reports showing patterns of coercive control. 
              Professional analysis powered by advanced AI technology.
            </p>
          </div>

          {/* Mission Statement */}
          <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 rounded-lg p-8 mb-12 border border-purple-500/30">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">Our Mission</h2>
            <p className="text-lg text-gray-300 leading-relaxed text-center">
              We believe that everyone deserves to have their experiences validated and understood. 
              Coercive control can be difficult to recognize and even harder to document. Our AI-powered 
              analysis helps transform scattered communications into clear, organized reports that reveal 
              patterns of psychological manipulation and control.
            </p>
          </div>

          {/* Key Features */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            
            <div className="bg-slate-800/50 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Shield className="w-8 h-8 text-blue-400" />
                <h3 className="text-2xl font-bold text-white">Secure & Private</h3>
              </div>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Automatic 24-hour data deletion</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>End-to-end encryption</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>No data sharing or selling</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>HTTPS secure transmission</span>
                </li>
              </ul>
            </div>

            <div className="bg-slate-800/50 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <Zap className="w-8 h-8 text-yellow-400" />
                <h3 className="text-2xl font-bold text-white">Advanced AI Analysis</h3>
              </div>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>19 categories of coercive control</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Pattern timeline analysis</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Professional PDF reports</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span>Risk assessment scoring</span>
                </li>
              </ul>
            </div>

          </div>

          {/* How It Helps */}
          <div className="bg-slate-800/50 rounded-lg p-8 mb-12">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">How We Help</h2>
            <div className="grid md:grid-cols-3 gap-6">
              
              <div className="text-center">
                <div className="bg-purple-600/20 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Eye className="w-8 h-8 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Make Patterns Visible</h3>
                <p className="text-gray-300">
                  Transform scattered communications into clear evidence of psychological manipulation patterns.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-blue-600/20 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Protect Your Privacy</h3>
                <p className="text-gray-300">
                  Complete confidentiality with automatic data deletion and no manual review of your content.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-green-600/20 rounded-full p-4 w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Provide Professional Reports</h3>
                <p className="text-gray-300">
                  Generate comprehensive, organized reports suitable for legal, therapeutic, or personal use.
                </p>
              </div>

            </div>
          </div>

          {/* Important Disclaimer */}
          <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-6 mb-8">
            <div className="flex items-center space-x-2 mb-4">
              <AlertCircle className="w-6 h-6 text-amber-400" />
              <h3 className="text-xl font-semibold text-amber-300">Important Notice</h3>
            </div>
            <div className="text-amber-200 space-y-2">
              <p>
                Our analysis is for <strong>informational and educational purposes only</strong>. 
                Reports are not legal advice, therapy, or clinical diagnosis.
              </p>
              <p>
                Always consult qualified professionals before making legal or mental health decisions. 
                If you're experiencing abuse, please reach out to local support services or emergency services.
              </p>
            </div>
          </div>

          {/* Values */}
          <div className="bg-slate-800/50 rounded-lg p-8">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">Our Values</h2>
            <div className="space-y-4 text-gray-300">
              <div>
                <h4 className="text-lg font-semibold text-purple-300 mb-2">Privacy First</h4>
                <p>Your data is automatically deleted within 24 hours. We never store, share, or manually review your communications.</p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-blue-300 mb-2">Empowerment Through Technology</h4>
                <p>Advanced AI helps reveal patterns that might be difficult to see or articulate, giving you tools to understand your experiences.</p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-green-300 mb-2">Accessible Support</h4>
                <p>Professional-quality analysis accessible 24/7, helping you document and understand communication patterns at your own pace.</p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-amber-300 mb-2">Responsible Technology</h4>
                <p>We're committed to ethical AI use, transparent processes, and clear limitations of our analysis capabilities.</p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AboutPage;